import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { postIntake, newKey, EMAIL_RE } from "@/lib/intakeApi";
import { Container, Eyebrow, PrimaryButton } from "@/components/site/Primitives";
import { Wordmark } from "@/components/site/Nav";
import { IntakeClosed, useIntakeOpen } from "@/components/forms/IntakeClosed";
import { Check, CountrySelect, Field, FormAlert, Honeypot, describe, inputClass } from "@/components/forms/Fields";

export const TRACKS = [["internal_team", "Internal AI team"], ["expert_partner", "Expert partner / advisor"]];
export const EXPERTISE = [
  ["ai_agents", "AI agents & assistants"], ["workflow_automation", "Workflow automation"], ["ai_integrations", "AI integrations"],
  ["data_analytics", "Data & analytics"], ["software_engineering", "Software engineering"], ["ai_strategy", "AI strategy & process design"],
  ["ai_evaluation", "AI evaluation & quality"], ["other", "Other"],
];
export const EXPERIENCE = [["lt_1", "Under 1 year"], ["1_3", "1–3 years"], ["3_5", "3–5 years"], ["5_plus", "5+ years"]];
const EMPTY = { full_name: "", email: "", country_of_residence: "", track: "", expertise: [], expertise_other: "", experience: "", profile_url: "", note: "", consent: false, website: "" };

function validate(f) {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Enter your full name.";
  if (!EMAIL_RE.test(f.email.trim())) e.email = "Enter a valid email address.";
  if (!f.country_of_residence) e.country_of_residence = "Select your country of residence.";
  if (!f.track) e.track = "Choose a track.";
  if (!f.expertise.length) e.expertise = "Choose at least one area.";
  if (f.expertise.includes("other") && !f.expertise_other.trim()) e.expertise_other = "Describe your other area.";
  if (!f.experience) e.experience = "Choose your experience.";
  if (f.profile_url.trim() && !/^https?:\/\/\S+\.\S+$/.test(f.profile_url.trim())) e.profile_url = "Enter a full link starting with https://";
  if (!f.consent) e.consent = "Please agree so we can contact you about your application.";
  return e;
}

const Choice = ({ type, name, value, checked, onChange, label, testId }) => (
  <label className={`fa-option flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-[14px] ${checked ? "border-fg/60 text-fg" : "border-fg/10 text-fg/75 hover:border-fg/30"}`}>
    <input type={type} name={name} value={value} checked={checked} onChange={onChange} data-testid={testId} className="h-4 w-4 accent-white" />
    {label}
  </label>
);

function Confirmation({ name }) {
  return (
    <div data-testid="talent-confirmation" role="status" className="max-w-2xl">
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-tone">Application received</p>
      <h1 className="mt-4 font-display text-3xl font-light tracking-[-0.03em] sm:text-4xl">Thanks{name ? `, ${name}` : ""}. We’ve received your application.</h1>
      <p className="mt-5 text-[15px] leading-relaxed text-subtle">
        Our team will review it. If there’s a potential fit, the next stage may ask for more detail about your experience or
        examples of your work. Submitting this form does not mean you have been accepted or hired.
      </p>
      <Link to="/" className="fa-link mt-8 inline-flex items-center gap-2 rounded-sm text-[14px] text-fg underline underline-offset-4">Back to home</Link>
    </div>
  );
}

export default function Careers() {
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState("");
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const key = useRef(newKey());
  const open = useIntakeOpen();

  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const text = (k) => ({ value: f[k], onChange: (e) => set(k, e.target.value) });
  const toggle = (id) => set("expertise", f.expertise.includes(id) ? f.expertise.filter((x) => x !== id) : [...f.expertise, id]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pending) return;
    const v = validate(f);
    setErrors(v);
    if (Object.keys(v).length) return setAlert("Please complete the highlighted fields.");
    setPending(true);
    setAlert("");
    const opt = (s) => (s.trim() ? s.trim() : null);
    const res = await postIntake("/talent", {
      full_name: f.full_name.trim(), email: f.email.trim(), country_of_residence: f.country_of_residence, track: f.track,
      expertise: f.expertise, expertise_other: f.expertise.includes("other") ? opt(f.expertise_other) : null,
      experience: f.experience, profile_url: opt(f.profile_url), note: opt(f.note), consent: f.consent,
      idempotency_key: key.current, website: f.website || null,
    });
    setPending(false);
    if (!res.ok) { setErrors(res.fieldErrors); return setAlert(res.message); }
    setDone(true);
    window.scrollTo({ top: 0 });
  };

  return (
    <div data-testid="careers-page" className="theme-dark min-h-screen bg-canvas text-fg">
      <header className="border-b border-fg/[0.06]">
        <Container className="flex h-16 items-center justify-between">
          <Link to="/" aria-label="FreelancAI home" className="rounded-sm"><Wordmark /></Link>
          <Link to="/" className="fa-link inline-flex items-center gap-2 rounded-sm text-[13px] text-subtle hover:text-fg"><ArrowLeft size={14} aria-hidden /> Home</Link>
        </Container>
      </header>
      <Container className="pb-24 pt-14">
        {done ? <Confirmation name={f.full_name.trim().split(" ")[0]} /> : open !== true ? <IntakeClosed loading={open === null} /> : (
          <form onSubmit={onSubmit} noValidate className="relative max-w-2xl">
            <Eyebrow className="text-tone">Careers</Eyebrow>
            <h1 className="mt-5 font-display text-4xl font-light tracking-[-0.04em] sm:text-5xl">Join our AI team</h1>
            <p className="mt-5 text-[15px] leading-relaxed text-subtle">
              We work with internal AI experts and trusted AI partners to deliver working solutions for businesses and
              organizations. Tell us briefly about yourself — no CV or documents needed at this stage.
            </p>
            <div className="mt-10 grid gap-6 sm:grid-cols-2">
              <Field id="t_name" label="Full name" required error={errors.full_name}>
                <input {...describe("t_name", errors.full_name)} {...text("full_name")} autoComplete="name" maxLength={120} data-testid="talent-full-name" className={inputClass} />
              </Field>
              <Field id="t_email" label="Email" required error={errors.email}>
                <input {...describe("t_email", errors.email)} {...text("email")} type="email" autoComplete="email" maxLength={254} data-testid="talent-email" className={inputClass} />
              </Field>
              <Field id="t_country" label="Country of residence" required error={errors.country_of_residence}>
                <CountrySelect id="t_country" value={f.country_of_residence} onChange={(v) => set("country_of_residence", v)} error={errors.country_of_residence} testId="talent-country" />
              </Field>
              <Field id="t_experience" label="Experience" required error={errors.experience}>
                <select {...describe("t_experience", errors.experience)} {...text("experience")} data-testid="talent-experience" className={inputClass}>
                  <option value="">Select</option>
                  {EXPERIENCE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
            </div>
            <fieldset className="mt-8" aria-describedby={errors.track ? "track-error" : undefined}>
              <legend className="text-[13px] text-fg">Track <span className="text-subtle">*</span></legend>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {TRACKS.map(([v, l]) => <Choice key={v} type="radio" name="track" value={v} checked={f.track === v} onChange={() => set("track", v)} label={l} testId={`talent-track-${v}`} />)}
              </div>
              {errors.track && <p id="track-error" className="mt-1.5 text-[13px] text-danger">{errors.track}</p>}
            </fieldset>
            <fieldset className="mt-8" aria-describedby={errors.expertise ? "expertise-error" : undefined}>
              <legend className="text-[13px] text-fg">Areas of expertise <span className="text-subtle">*</span></legend>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {EXPERTISE.map(([v, l]) => <Choice key={v} type="checkbox" name="expertise" value={v} checked={f.expertise.includes(v)} onChange={() => toggle(v)} label={l} testId={`talent-expertise-${v}`} />)}
              </div>
              {errors.expertise && <p id="expertise-error" className="mt-1.5 text-[13px] text-danger">{errors.expertise}</p>}
            </fieldset>
            {f.expertise.includes("other") && (
              <div className="mt-4">
                <Field id="t_other" label="Other area" required error={errors.expertise_other}>
                  <input {...describe("t_other", errors.expertise_other)} {...text("expertise_other")} maxLength={80} data-testid="talent-expertise-other" className={inputClass} />
                </Field>
              </div>
            )}
            <div className="mt-8 grid gap-6">
              <Field id="t_url" label="LinkedIn or portfolio URL" error={errors.profile_url}>
                <input {...describe("t_url", errors.profile_url)} {...text("profile_url")} type="url" placeholder="https://" maxLength={300} data-testid="talent-profile-url" className={inputClass} />
              </Field>
              <Field id="t_note" label="Short note" error={errors.note} hint={`${f.note.length}/500`}>
                <textarea {...describe("t_note", errors.note, true)} {...text("note")} rows={4} maxLength={500} data-testid="talent-note" className={`${inputClass} resize-y`} />
              </Field>
              <Check id="t_consent" checked={f.consent} onChange={(v) => set("consent", v)} error={errors.consent} testId="talent-consent">
                I agree that FreelancAI may contact me about this application.
              </Check>
              <p className="text-[12px] leading-relaxed text-subtle">
                We store these details only to review your application and contact you about it. Please don’t include
                sensitive personal information. See our <Link to="/privacy" data-testid="talent-privacy-link" className="fa-link rounded-sm text-fg underline underline-offset-4">privacy notice</Link>.
              </p>
            </div>
            <Honeypot value={f.website} onChange={(v) => set("website", v)} />
            <div className="mt-8 space-y-4">
              <FormAlert message={alert} testId="talent-form-alert" />
              <PrimaryButton type="submit" disabled={pending} aria-busy={pending} data-testid="talent-submit-button">
                {pending ? "Sending…" : "Submit application"}
              </PrimaryButton>
            </div>
          </form>
        )}
      </Container>
    </div>
  );
}
